import { useSuspenseQuery } from '@tanstack/react-query';
import {
  SourcingService,
  type SourcingChannelDetails,
  type IndeedIntegrationSettings,
} from 'api-clients/monolith';
import _range from 'lodash/range';
import { Fragment, ReactNode, Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import EmptyChannels from '@/assets/icons/empty-channels.svg';
import InformationCircle from '@/assets/icons/information-circle.svg';
import { Counter } from '@/components/ui/counter';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useAuthenticatedContext } from '@/contexts/auth-context';
import { queryKeys } from '@/query-keys';
import { useIndeedSettings } from '@/hooks/use-indeed-settings';
import { useSourceIndeedContract } from '@/hooks/use-source-indeed-contract';
import { INDEED, VONQ } from '@/types/channels';
import { reportBoundaryError } from '@/utils/sentry';

import { IndeedOrganicRow } from './indeed-organic-row';
import { IntegratedChannelsTableHeader } from './table-header';
import { IntegratedChannelsTableRow } from './table-row';

/* Layout component with all static content, to avoid code duplication in skeleton */
function IntegratedChannelsLayout({
  children,
  counter,
}: {
  children: ReactNode;
  counter: ReactNode;
}) {
  return (
    <div className="border-fx-neutral-200 flex flex-col rounded-md border bg-white">
      <div className="flex items-center justify-between p-4">
        {/* Title and description */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Typography variant="medium2" as="h2">
              <FormattedMessage
                defaultMessage="Integrated channels"
                description="Title for the Integrated channels"
              />
            </Typography>
            {counter}
          </div>
          <Typography variant="body1">
            <FormattedMessage
              defaultMessage="Integrated channels help you to post jobs, and track applications, spend data."
              description="Description for the Integrated channels"
            />
          </Typography>
        </div>
      </div>

      {children}
    </div>
  );
}

export function IntegratedChannels() {
  const indeedEnabled = useSourceIndeedContract();
  const { data } = useSuspenseQuery({
    queryKey: [...queryKeys.settings.channels.integrated],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannelsAllChannels(),
  });

  const legacy = <IntegratedChannelsList channels={data.channels} />;
  if (!indeedEnabled) return legacy;
  return (
    <ErrorBoundary
      onError={reportBoundaryError('settings.integrated-channels')}
      fallback={legacy}
    >
      <Suspense fallback={legacy}>
        <IndeedIntegratedChannels channels={data.channels} />
      </Suspense>
    </ErrorBoundary>
  );
}

function IndeedIntegratedChannels({
  channels,
}: {
  channels: SourcingChannelDetails[];
}) {
  const { data, isError } = useIndeedSettings();
  return (
    <IntegratedChannelsList
      channels={channels}
      indeedSettings={isError ? undefined : data}
    />
  );
}

function IntegratedChannelsList({
  channels,
  indeedSettings,
}: {
  channels: SourcingChannelDetails[];
  indeedSettings?: IndeedIntegrationSettings;
}) {
  const intl = useIntl();
  const { isSuperAdmin } = useAuthenticatedContext();

  const { standardChannels, vonqChannel } = useMemo(() => {
    const eligible = channels.filter(
      channel => channel.is_integratable || channel.is_publishable,
    );

    return {
      standardChannels: eligible.filter(c => c.slug !== VONQ),
      vonqChannel: eligible.find(c => c.slug === VONQ) ?? null,
    };
  }, [channels]);

  const showOrganic =
    !!indeedSettings &&
    standardChannels.some(channel => channel.slug === INDEED);
  const totalCount =
    standardChannels.length +
    (isSuperAdmin && vonqChannel ? 1 : 0) +
    (showOrganic ? 1 : 0);

  return (
    <>
      <IntegratedChannelsLayout
        counter={totalCount > 0 && <Counter count={totalCount} />}
      >
        {/* Table headers */}
        <IntegratedChannelsTableHeader />

        {/* Empty state */}
        {totalCount === 0 && (
          <EmptyState
            className="w-full"
            icon={EmptyChannels}
            header={intl.formatMessage({
              defaultMessage: 'No channels configured yet!',
              description:
                'Empty state for the integrated (paid) channel table',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Create your first non-integrated channel to group UTM sources and track their performance.',
              description:
                'Empty-state body on channel settings encouraging first UTM-grouped channel setup',
            })}
          />
        )}

        {/* Standard channel rows */}
        {standardChannels.map(channel => (
          <Fragment key={channel.slug}>
            <IntegratedChannelsTableRow channel={channel} />
            {channel.slug === INDEED && indeedSettings && (
              <IndeedOrganicRow settings={indeedSettings} />
            )}
          </Fragment>
        ))}

        {/* Vonq channel — super-admin only */}
        {isSuperAdmin && vonqChannel && (
          <div className="border-fx-lemon border">
            <div className="bg-fx-lemon-50 border-fx-lemon flex items-center gap-1.5 border-b px-4 py-1.5">
              <img src={InformationCircle} alt="" className="h-4 w-4" />
              <Typography variant="subtitle1" className="text-fx-lemon-800">
                <FormattedMessage
                  defaultMessage="Super-admin only"
                  description="Label above Vonq channel row indicating it is only visible to super admins"
                />
              </Typography>
            </div>
            <IntegratedChannelsTableRow channel={vonqChannel} isVonq />
          </div>
        )}
      </IntegratedChannelsLayout>
    </>
  );
}

IntegratedChannels.Skeleton = function IntegratedChannelsSkeleton() {
  return (
    <IntegratedChannelsLayout
      counter={<Skeleton className="h-4 w-6 rounded-full" />}
    >
      <IntegratedChannelsTableHeader />

      {_range(3).map(index => (
        <IntegratedChannelsTableRow.Skeleton key={index} />
      ))}
    </IntegratedChannelsLayout>
  );
};
